import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

const baseUrl = "/api/CityObjectMvr";

export interface MvrChartPoint {
  week: string | null;
  percentage: number;
}

export interface MvrRoundListItem {
  id: number;
  status: number; // 1 draft, 2 ready
  week: string | null;
  mvrDate: string;
  creatorName: string | null;
  percentage: number;
  noticeCount: number;
}

export interface MvrOverview {
  objectId: number;
  contractId: number;
  objectName: string | null;
  target: number;
  averagePercentage: number;
  chartPoints: MvrChartPoint[];
  rounds: MvrRoundListItem[];
  canDelete: boolean;
  canRelease: boolean;
}

export interface MvrGroupResult {
  groupId: number;
  groupName: string | null;
  correctCount: number;
  incorrectCount: number;
}

export interface MvrNotice {
  id: number;
  mvrMeasurementId: number;
  groupId: number;
  groupName: string | null;
  itemId: number;
  itemName: string | null;
  description: string | null;
  dueDate: string | null;
  responsibleUserId: number | null;
  responsibleUserName: string | null;
  fixed: string | null;
  fixedDescription: string | null;
}

export interface MvrRoundDetail {
  id: number;
  objectId: number;
  contractId: number;
  status: number;
  mvrDate: string;
  canModify: boolean;
  totalCorrect: number;
  totalIncorrect: number;
  percentage: number;
  groups: MvrGroupResult[];
  notices: MvrNotice[];
}

export interface MvrItemOption {
  id: number;
  name: string | null;
}

export interface MvrUserOption {
  id: number;
  name: string | null;
}

export interface MvrNoticeOptions {
  groupId: number;
  groupName: string | null;
  items: MvrItemOption[];
  responsibleUsers: MvrUserOption[];
}

export interface MvrIdentity {
  objectId: number;
  contractId: number;
  userId: number;
  addressSystemId: number;
}

export interface CreateMvrRoundRequest extends MvrIdentity {}

export interface MvrRoundWorkflowRequest extends MvrIdentity {
  id: number;
}

export interface UpdateMvrCounterRequest extends MvrIdentity {
  id: number;
  groupId: number;
  counterType: number; // 1 correct, 2 incorrect
  change: number; // +1 / -1
}

export interface SaveMvrNoticeRequest extends MvrIdentity {
  id: number; // 0 = new
  mvrMeasurementId: number;
  mvrItemId: number;
  responsibleUserId: number | null;
  dueDate: string | null;
  description: string | null;
  fixed: string | null;
  fixedDescription: string | null;
}

export interface DeleteMvrNoticeRequest extends MvrIdentity {
  id: number;
  mvrMeasurementId: number;
}

const authHeaders = async () => {
  const token = await getApiAccessToken();
  return { headers: { Authorization: `Bearer ${token}` } };
};

/** Overview (chart + average + round list) for an object + contract. */
export const getMvrOverview = async (
  objectId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<MvrOverview> => {
  const response = await apiClient.get<MvrOverview>(`${baseUrl}/${objectId}`, {
    params: { contractId, userId, addressSystemId },
    ...(await authHeaders()),
  });
  return response.data;
};

/** Full detail of a single round. */
export const getMvrRound = async (
  id: number,
  objectId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<MvrRoundDetail> => {
  const response = await apiClient.get<MvrRoundDetail>(`${baseUrl}/round/${id}`, {
    params: { objectId, contractId, userId, addressSystemId },
    ...(await authHeaders()),
  });
  return response.data;
};

/** Creates a new empty round. Returns the new round id. */
export const createMvrRound = async (
  request: CreateMvrRoundRequest,
): Promise<number> => {
  const response = await apiClient.post<{ id: number }>(
    `${baseUrl}/round`,
    request,
    await authHeaders(),
  );
  return response.data.id;
};

/** Adjusts one group's correct/incorrect counter by +1/-1. */
export const updateMvrCounter = async (
  request: UpdateMvrCounterRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/counter`, request, await authHeaders());
};

/** Marks a draft round as ready (status 1 → 2). */
export const markMvrRoundReady = async (
  request: MvrRoundWorkflowRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/ready`, request, await authHeaders());
};

/** Releases a ready round back to draft (status 2 → 1). */
export const releaseMvrRound = async (
  request: MvrRoundWorkflowRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/release`, request, await authHeaders());
};

/** Deletes a draft round. */
export const deleteMvrRound = async (
  request: MvrRoundWorkflowRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/delete`, request, await authHeaders());
};

/** Notice form options (defect items + responsible people) for a group. */
export const getMvrNoticeOptions = async (
  groupId: number,
  objectId: number,
  contractId: number,
  userId: number,
  addressSystemId: number,
): Promise<MvrNoticeOptions> => {
  const response = await apiClient.get<MvrNoticeOptions>(
    `${baseUrl}/notice-options`,
    {
      params: { groupId, objectId, contractId, userId, addressSystemId },
      ...(await authHeaders()),
    },
  );
  return response.data;
};

/** Creates or updates a notice. Returns the notice id. */
export const saveMvrNotice = async (
  request: SaveMvrNoticeRequest,
): Promise<number> => {
  const response = await apiClient.post<{ id: number }>(
    `${baseUrl}/notice`,
    request,
    await authHeaders(),
  );
  return response.data.id;
};

/** Deletes a notice. */
export const deleteMvrNotice = async (
  request: DeleteMvrNoticeRequest,
): Promise<void> => {
  await apiClient.post(`${baseUrl}/notice/delete`, request, await authHeaders());
};
